import { NotablePlainDateAsMidnightUtcSchema } from 'notable-common';
import { z } from 'zod';
import { EligibilityCheckStatus } from './eligibilityCheckStatus.js';
import { InsurancePlanType } from './planType.js';
import { InsuranceSource } from './source.js';
import { EmploymentStatus, Relationship, SexUpper } from '../demographics/index.js';
/**
 * A patient's insurance record as primary serves it — one coverage, plus the policy holder behind
 * it. A patient may hold several; `primary` marks which one bills first.
 *
 * Only the identity columns, `primary`, and `source` are guaranteed: the rest is assembled from
 * whatever the EHR or the patient supplied. `payor` is the payor's name as an unconstrained string —
 * some writers fill it from primary's `Payor` enum, the column enforces nothing — and it is
 * unrelated to `payorId`, which points at a payor record.
 *
 * The `primaryPolicyHolder*` fields describe the subscriber, who is often but not always the
 * patient — `patientRelationshipToPrimaryPolicyholder` says which.
 */
export const PatientInsuranceInformationSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    ehrInformationId: z.uuid(),
    insuranceProviderId: z.uuid(),
    primary: z.boolean(),
    source: z.enum(InsuranceSource),
    memberId: z.string().nullable(),
    groupNumber: z.string().nullable(),
    groupName: z.string().nullable(),
    planType: z.enum(InsurancePlanType).nullable(),
    planName: z.string().nullable(),
    nameRaw: z.string().nullable(),
    payor: z.string().nullable(),
    payorId: z.uuid().nullable(),
    payorPlanId: z.uuid().nullable(),
    ehrPlanId: z.string().nullable(),
    eligibilityStatusCheck: z.enum(EligibilityCheckStatus).nullable(),
    eligibilitySubmitDate: NotablePlainDateAsMidnightUtcSchema.nullable(),
    coverageStartDate: NotablePlainDateAsMidnightUtcSchema.nullable(),
    coverageEndDate: NotablePlainDateAsMidnightUtcSchema.nullable(),
    primaryCopay: z.number().int().nullable(),
    specialtyCopay: z.number().int().nullable(),
    lastUpdatedInEhrDate: z.iso.datetime().nullable(),
    deletedTimestamp: z.iso.datetime().nullable(),
    primaryPolicyHolderFirstName: z.string().nullable(),
    primaryPolicyHolderMiddleName: z.string().nullable(),
    primaryPolicyHolderLastName: z.string().nullable(),
    primaryPolicyHolderDob: NotablePlainDateAsMidnightUtcSchema.nullable(),
    primaryPolicyHolderSex: z.enum(SexUpper).nullable(),
    primaryPolicyHolderMemberId: z.string().nullable(),
    primaryPolicyHolderAddressId: z.uuid().nullable(),
    primaryPolicyHolderHomePhone: z.string().nullable(),
    primaryPolicyHolderWorkPhone: z.string().nullable(),
    primaryPolicyHolderEmployer: z.string().nullable(),
    primaryPolicyHolderEmploymentStatus: z.enum(EmploymentStatus).nullable(),
    primaryPolicyHolderEmploymentStatusRaw: z.string().nullable(),
    primaryPolicyHolderRetirementDate: NotablePlainDateAsMidnightUtcSchema.nullable(),
    primaryPolicyHolderRelationshipToGuarantor: z.enum(Relationship).nullable(),
    primaryPolicyHolderRelationshipToGuarantorRaw: z.string().nullable(),
    patientRelationshipToPrimaryPolicyholder: z.enum(Relationship).nullable(),
});
//# sourceMappingURL=patientInsuranceInformation.js.map